import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useGetFilteredPrompts } from "./prompt";

/**
 * Hook for pages/chat to get prompts matching the current context.
 *
 * Calls GET /api/prompts/filtered with the provided context, returning only
 * prompts whose rules match. The backend handles the matching logic:
 * - AND across dimensions (all specified rules must match)
 * - OR within a dimension (any value in the rule list is sufficient)
 * - null/empty = match any (no restriction for that dimension)
 *
 * @param {Object} context - The current UI context to filter prompts by.
 *   All fields are optional — omit or set to null to skip filtering on that dimension.
 * @param {string} [context.location]     - "disclosure" | "ai-draft" | "chat"
 * @param {string} [context.page]         - Route path relative to /app/ (e.g., "issb/data-input/answer")
 * @param {string} [context.stage]        - Question workflow stage (e.g., "START", "IN_PROGRESS", "COMPLETE")
 * @param {string} [context.completeness] - "NONE" | "LOW" | "MEDIUM" | "HIGH"
 * @param {string} [context.confidence]   - "NONE" | "LOW" | "MEDIUM" | "HIGH"
 * @param {Object} [options]              - React Query options (e.g., { enabled, staleTime })
 * @returns {{ prompts: Array<{id, name, content, description, rules}>, isLoading: boolean }}
 *
 * @example
 * // In AIChatWidget — get prompts for the chat on the answer page
 * const { prompts } = useContextualPrompts({
 *   location: "chat",
 *   page: "issb/data-input/answer",
 *   stage: aiContext?.stageType,
 *   completeness: aiContext?.completeness,
 *   confidence: aiContext?.confidence,
 * });
 *
 * @example
 * // In AnswerDetailPage — get prompts for the disclosure tab
 * const { prompts } = useContextualPrompts({
 *   location: "disclosure",
 *   page: "issb/data-input/answer",
 *   stage: questionData?.stageType,
 * });
 *
 * @example
 * // On a page with no question context — just filter by page
 * const { prompts } = useContextualPrompts({
 *   location: "chat",
 *   page: "issb/dashboard",
 * });
 *
 * Adding a new dimension:
 *   1. Add the field to the context parameter here
 *   2. Include it in the filters object below
 *   3. Add it to getFilteredPrompts() in frontend/src/api/prompt/prompt.js
 *   4. See PromptRules.java (backend) for the full checklist
 */
// Known page values that match the PAGE_OPTIONS in PromptModal.
// Ordered longest-first so "issb/data-input/answer" matches before "issb/data-input".
const KNOWN_PAGES = [
  "issb/data-input/answer",
  "issb/data-input",
  "issb/dashboard",
  "issb/disclosure",
  "issb/ai-review",
  "issb/ai-tasks",
  "issb/sources",
  "issb/audit/start",
  "issb/requirements",
  "issb/report-doc",
];

function detectPage(pathname) {
  if (!pathname.startsWith("/app/")) return pathname;
  const path = pathname.slice(5); // strip "/app/"
  // Match against known pages, or fall back to raw path so prompts
  // with specific page rules won't match on unrelated pages.
  return KNOWN_PAGES.find((p) => path === p || path.startsWith(p + "/")) || path;
}

export function useContextualPrompts(context, options = {}) {
  const { pathname } = useLocation();
  const autoPage = detectPage(pathname);

  const filters = useMemo(
    () => ({
      location: context?.location || null,
      page: context?.page ?? autoPage,
      stage: context?.stage || null,
      completeness: context?.completeness || null,
      confidence: context?.confidence || null,
    }),
    [context?.location, context?.page, autoPage, context?.stage, context?.completeness, context?.confidence]
  );

  const { data: prompts = [], isLoading } = useGetFilteredPrompts(filters, {
    enabled: !!context,
    ...options,
  });

  return { prompts, isLoading };
}
